import { useRef, useEffect, useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { AuthProvider, useAuth } from '@/context/AuthContext';
import { Toaster } from '@/components/ui/sonner';
import LandingPage from '@/pages/LandingPage';
import Dashboard from '@/pages/Dashboard';
import QuoteWizard from '@/pages/QuoteWizard';
import CheckGenerale from '@/pages/CheckGenerale';
import Abbonamento from '@/pages/Abbonamento';
import AIChat from '@/pages/AIChat';
import CalendarBooking from '@/pages/CalendarBooking';
import PaymentChoice from '@/pages/PaymentChoice';
import MyAppointments from '@/pages/MyAppointments';
import AdminDashboard from '@/pages/AdminDashboard';
import AziendePage from '@/pages/AziendePage';
import CheckInfo from '@/pages/CheckInfo';
import InterventoInfo from '@/pages/InterventoInfo';
import BookingLink from '@/pages/BookingLink';
import '@/App.css';

function AuthCallback() {
  const hasProcessed = useRef(false);
  const { exchangeSession } = useAuth();
  const navigate = useNavigate();
  const [error, setError] = useState(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    // Use window.location.hash directly for maximum reliability
    const hash = window.location.hash;
    const sessionId = new URLSearchParams(hash.substring(1)).get('session_id');

    if (!sessionId) {
      navigate('/', { replace: true });
      return;
    }

    (async () => {
      const success = await exchangeSession(sessionId);
      if (success) {
        // Clean hash from URL, then navigate
        window.history.replaceState(null, '', window.location.pathname);
        navigate('/dashboard', { replace: true });
      } else {
        setError(true);
        setTimeout(() => navigate('/', { replace: true }), 2000);
      }
    })();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-stone-50">
        <div className="flex flex-col items-center gap-3">
          <p className="text-red-500 text-sm font-medium">Errore di autenticazione. Riprovare...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-center min-h-screen bg-stone-50">
      <div className="flex flex-col items-center gap-3">
        <div className="w-8 h-8 border-3 border-blue-600 border-t-transparent rounded-full animate-spin" />
        <p className="text-slate-500 text-sm font-medium">Accesso in corso...</p>
      </div>
    </div>
  );
}

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-stone-50">
        <div className="flex flex-col items-center gap-3">
          <div className="w-8 h-8 border-3 border-blue-600 border-t-transparent rounded-full animate-spin" />
          <p className="text-slate-500 text-sm font-medium">Caricamento...</p>
        </div>
      </div>
    );
  }
  if (!user) return <Navigate to="/" replace />;
  return children;
}

function AppRouter() {
  // CRITICAL: Check for session_id synchronously during render using BOTH
  // React Router location AND window.location for maximum reliability.
  const location = useLocation();
  const hashHasSession = location.hash?.includes('session_id=') || window.location.hash?.includes('session_id=');

  if (hashHasSession) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/check-info" element={<CheckInfo />} />
      <Route path="/intervento-info" element={<InterventoInfo />} />
      <Route path="/aziende" element={<AziendePage />} />
      <Route path="/prenota/:token" element={<BookingLink />} />
      <Route path="/dashboard" element={<Dashboard />} />
      <Route path="/preventivo" element={<QuoteWizard />} />
      <Route path="/check-generale" element={<CheckGenerale />} />
      <Route path="/abbonamento" element={<Abbonamento />} />
      <Route path="/chat/:quoteId" element={<AIChat />} />
      <Route path="/calendario/:quoteId" element={<CalendarBooking />} />
      <Route path="/pagamento/:quoteId" element={<PaymentChoice />} />
      <Route path="/appuntamenti" element={<MyAppointments />} />
      <Route path="/admin" element={<ProtectedRoute><AdminDashboard /></ProtectedRoute>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRouter />
        <Toaster richColors position="top-right" />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
